import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, HardHat, MapPin, Trash2, LineChart as LineChartIcon, FileSpreadsheet, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Bar, ComposedChart, CartesianGrid } from "recharts";
import { ClienteForm, clienteEmpty } from "@/pages/Clienti";

const empty = { nome: "", cliente: "", cliente_id: null, indirizzo: "", descrizione: "", budget: 0, stato: "in_corso", data_inizio: "", data_fine_prevista: "" };
const emptyC = empty;
const statusColors = {
  in_corso: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  in_pausa: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  completato: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200",
  in_ritardo: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

export default function Cantieri() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyC);
  const [timeline, setTimeline] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [newClienteOpen, setNewClienteOpen] = useState(false);
  const [newCliente, setNewCliente] = useState(clienteEmpty);
  const router = useNavigate();
  const canEdit = user?.role === "admin" || user?.role === "capocantiere";

  const openTimeline = async (c) => {
    try {
      const { data } = await api.get(`/cantieri/${c.id}/timeline?mesi=6`);
      setTimeline({ nome: c.nome, data: data.timeline });
    } catch { toast.error("Errore timeline"); }
  };

  const load = async () => {
    const [c, cl] = await Promise.all([api.get("/cantieri"), api.get("/clienti").catch(() => ({ data: [] }))]);
    setList(c.data);
    setClienti(cl.data);
    const s = {};
    await Promise.all(c.data.map(async (ca) => {
      try { const { data: sum } = await api.get(`/cantieri/${ca.id}/summary`); s[ca.id] = sum; } catch {}
    }));
    setSummaries(s);
  };
  useEffect(() => { load(); }, []);

  const saveNewCliente = async () => {
    try {
      const { data } = await api.post("/clienti", newCliente);
      toast.success("Cliente creato");
      setClienti((c) => [...c, data]);
      setForm((f) => ({ ...f, cliente: data.label || "", cliente_id: data.id }));
      setNewCliente(clienteEmpty); setNewClienteOpen(false);
    } catch { toast.error("Errore"); }
  };

  const save = async () => {
    try {
      await api.post("/cantieri", { ...form, budget: Number(form.budget) });
      toast.success("Cantiere creato");
      setForm(emptyC); setOpen(false); load();
    } catch (e) { toast.error("Errore creazione cantiere"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo cantiere?")) return;
    try { await api.delete(`/cantieri/${id}`); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Operazioni</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Cantieri</h1>
          <p className="text-muted-foreground mt-1">Gestisci lavori attivi, budget e stato di avanzamento.</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-cantiere" className="h-11"><Plus className="h-4 w-4 mr-2" /> Nuovo cantiere</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuovo cantiere</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome</Label><Input data-testid="c-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
                <div><Label>Cliente</Label>
                  <Select
                    value={form.cliente_id || "manual"}
                    onValueChange={(v) => {
                      if (v === "manual") { setForm({ ...form, cliente_id: null }); return; }
                      if (v === "__new__") { setNewClienteOpen(true); return; }
                      const found = clienti.find((c) => c.id === v);
                      setForm({ ...form, cliente_id: v, cliente: found?.label || form.cliente });
                    }}
                  >
                    <SelectTrigger data-testid="c-cliente-select"><SelectValue placeholder="Scegli cliente" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">— Testo libero —</SelectItem>
                      <SelectItem value="__new__">+ Nuovo cliente...</SelectItem>
                      {clienti.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!form.cliente_id && <Input data-testid="c-cliente" className="mt-2" placeholder="Nome cliente" value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} />}
                </div>
                <div><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} /></div>
                <div><Label>Budget €</Label><Input data-testid="c-budget" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
                <div><Label>Stato</Label>
                  <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                    <SelectTrigger data-testid="c-stato"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_corso">In corso</SelectItem>
                      <SelectItem value="in_pausa">In pausa</SelectItem>
                      <SelectItem value="completato">Completato</SelectItem>
                      <SelectItem value="in_ritardo">In ritardo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Inizio</Label><Input type="date" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} /></div>
                <div><Label>Fine prevista</Label><Input type="date" value={form.data_fine_prevista} onChange={(e) => setForm({ ...form, data_fine_prevista: e.target.value })} /></div>
                <div className="col-span-2"><Label>Descrizione</Label><Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
              </div>
              <Button data-testid="c-save" onClick={save} className="w-full h-11 mt-2">Crea cantiere</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((c) => {
          const s = summaries[c.id];
          const pct = s && s.budget > 0 ? Math.min(100, (s.costi_totali / s.budget) * 100) : 0;
          return (
            <Card key={c.id} data-testid={`cantiere-card-${c.id}`} className="card-elevated group hover:border-primary transition-colors">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <HardHat className="h-4 w-4 text-primary" />
                      <h3 className="font-bold text-lg">{c.nome}</h3>
                    </div>
                    <div className="text-sm text-muted-foreground">{c.cliente}</div>
                  </div>
                  {isAdmin ? (
                    <Select
                      value={c.stato}
                      onValueChange={async (v) => {
                        try {
                          const payload = { ...c, stato: v, budget: Number(c.budget || 0) };
                          delete payload.id; delete payload.created_at; delete payload.created_by; delete payload.avanzamento_history;
                          await api.put(`/cantieri/${c.id}`, payload);
                          toast.success("Stato aggiornato");
                          load();
                        } catch { toast.error("Errore"); }
                      }}
                    >
                      <SelectTrigger
                        data-testid={`stato-${c.id}`}
                        className={`h-7 w-32 text-xs font-semibold border ${statusColors[c.stato] || ""}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_corso">In corso</SelectItem>
                        <SelectItem value="in_pausa">In pausa</SelectItem>
                        <SelectItem value="completato">Finito</SelectItem>
                        <SelectItem value="in_ritardo">In ritardo</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge data-testid={`stato-readonly-${c.id}`} className={`badge-tag ${statusColors[c.stato] || ""}`}>{(c.stato || "").replace("_", " ")}</Badge>
                  )}
                </div>
                {c.indirizzo && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {c.indirizzo}
                  </div>
                )}
                <div className={`grid gap-2 pt-2 ${isAdmin ? "grid-cols-3" : "grid-cols-1"}`}>
                  {isAdmin && <Stat label="Budget" value={fmt(c.budget || 0)} />}
                  {isAdmin && <Stat label="Costi" value={fmt(s?.costi_totali || 0)} tone="neg" />}
                  {isAdmin && <Stat label="Margine" value={fmt(s?.margine || 0)} tone={s?.margine >= 0 ? "pos" : "neg"} />}
                  {!isAdmin && <Stat label="Avanzamento" value={`${(s?.avanzamento_lavori || 0).toFixed(0)}%`} />}
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Ore registrate: {s?.ore_totali || 0}</span>
                  <span>{s?.n_rendiconti || 0} rendiconti</span>
                </div>
                {user?.role === "admin" && (
                  <Button variant="ghost" size="sm" className="w-full text-destructive hover:text-destructive" onClick={() => remove(c.id)}
                          data-testid={`del-cantiere-${c.id}`}>
                    <Trash2 className="h-3 w-3 mr-2" /> Elimina
                  </Button>
                )}
                <Button variant="outline" size="sm" className="w-full" onClick={() => router(`/cantieri/${c.id}`)} data-testid={`open-cantiere-${c.id}`}>
                  <FileSpreadsheet className="h-3 w-3 mr-2" /> Apri computo & avanzamento
                </Button>
                {isAdmin && (
                  <Button variant="outline" size="sm" className="w-full" onClick={() => openTimeline(c)} data-testid={`timeline-${c.id}`}>
                    <LineChartIcon className="h-3 w-3 mr-2" /> Timeline lavori vs incassi
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
        {list.length === 0 && <div className="text-muted-foreground col-span-full py-12 text-center">Nessun cantiere. Crea il primo.</div>}
      </div>

      <Dialog open={newClienteOpen} onOpenChange={setNewClienteOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Nuovo cliente</DialogTitle></DialogHeader>
          <ClienteForm form={newCliente} setForm={setNewCliente} />
          <Button data-testid="c-cliente-save" onClick={saveNewCliente} className="h-11 mt-2"><UserPlus className="h-4 w-4 mr-2" /> Salva cliente</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!timeline} onOpenChange={(o) => !o && setTimeline(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Timeline — {timeline?.nome}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Avanzamento lavori (linea) a confronto con fatture emesse e incassi mensili. Un gap persistente tra barre arancioni e verdi indica ritardo di pagamento.
          </p>
          <div className="h-80 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={timeline?.data || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <Tooltip
                  formatter={(v, name) => {
                    if (name === "Avanzamento") return `${v}%`;
                    return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="emesso" name="Fatture emesse" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="incassato" name="Incassato" fill="#16A34A" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="avanzamento" name="Avanzamento" stroke="#2563EB" strokeWidth={3} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {timeline?.data?.length > 0 && (() => {
            const last = timeline.data[timeline.data.length - 1];
            const gap = last.emesso - last.incassato;
            return (
              <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
                <div className="bg-secondary rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Avanzamento</div>
                  <div className="text-xl font-bold text-primary">{last.avanzamento}%</div>
                </div>
                <div className="bg-secondary rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Incassato (mese)</div>
                  <div className="text-xl font-bold text-emerald-600">{new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(last.incassato || 0)}</div>
                </div>
                <div className={`rounded-lg p-3 ${gap > 0 ? "bg-destructive/10" : "bg-emerald-100 dark:bg-emerald-900/30"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gap emesso-incassato</div>
                  <div className={`text-xl font-bold ${gap > 0 ? "text-destructive" : "text-emerald-700"}`}>
                    {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(gap || 0)}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-red-600" : "";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-bold text-sm tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
